import { ChevronUp } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useTranslations } from '../context/TranslationContext';
import useAnalytics from '../hooks/useAnalytics';

interface FAQ {
  question: string;
  answer: string;
}

const FAQItem: React.FC<{ faq: FAQ; index: number }> = ({ faq, index }) => {
  const { trackEvent } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const openTimeRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (isOpen) {
      // Track question opened
      trackEvent('faq_interaction', {
        action_type: 'open',
        question_index: index,
        question_text: faq.question
      });
      
      // Start timing how long the question stays open
      openTimeRef.current = Date.now();
    } else if (openTimeRef.current) {
      // Calculate time spent reading when closing
      const timeSpentMs = Date.now() - openTimeRef.current;
      const timeSpentSeconds = Math.round(timeSpentMs / 1000);
      
      if (timeSpentSeconds >= 2) { // Only track if they spent at least 2 seconds reading
        trackEvent('faq_engagement', {
          question_index: index,
          question_text: faq.question,
          time_spent_seconds: timeSpentSeconds
        });
      }
      
      openTimeRef.current = null;
    }
  }, [isOpen, index, faq.question, trackEvent]);

  const handleClick = () => {
    if (!isOpen) {
      trackEvent('faq_impression', {
        question_index: index,
        question_text: faq.question,
        timestamp: new Date().toISOString()
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden hover:shadow-md transition-shadow">
      <button
        className="w-full px-6 py-5 flex justify-between items-center focus:outline-none group"
        onClick={handleClick}
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-medium text-gray-900 text-left group-hover:text-[#597F70] transition-colors">
          {faq.question}
        </h3>
        <span 
          className={`ml-6 flex-shrink-0 transform transition-transform duration-300 ease-in-out ${
            isOpen ? 'rotate-90' : ''
          }`}
        >
          <ChevronUp className="w-6 h-6 text-[#597F70]" />
        </span>
      </button>
      <div
        className={`transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className={`px-6 pb-5 text-gray-600 ${isOpen ? 'animate-fadeIn' : ''}`}>
          {faq.answer}
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const { t } = useTranslations();
  const { trackEvent } = useAnalytics();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackEvent('faq_section_view', {
              timestamp: new Date().toISOString(),
              visible_questions: t.faq.items.length
            });
            observer.disconnect(); // Only track first view
          }
        });
      },
      { threshold: 0.1 } // Fire when 10% of the section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [trackEvent, t.faq.items.length]);

  return (
    <section id='faq' className="py-16 bg-slate-50" ref={sectionRef}>
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">{t.faq.title}</h2>
          <p className="text-gray-600 text-center mb-12">
            {t.faq.description}
          </p>
          <div className="space-y-4">
            {t.faq.items.map((faq, index) => (
              <FAQItem key={index} faq={faq} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;